import type { User } from "firebase/auth";
import type { PlanId, PlanLimits } from "./organizations";

export interface PlanCatalogItem extends PlanLimits {
  id: PlanId;
  marketingFeatures?: string[];
  highlighted?: boolean;
  stripeProductId?: string | null;
  stripePriceId?: string | null;
  intelligenceCapacityId?: "lite" | "plus" | "extra" | null;
  intelligenceDiscountPercent?: number | null;
  updatedAt?: string | null;
}

const getBackendBaseUrl = (): string => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (!raw) {
    throw new Error("Missing REACT_APP_BACKEND_URL environment variable.");
  }
  return raw.replace(/\/+$/, "");
};

export const fetchPlanCatalog = async (user: User): Promise<PlanCatalogItem[]> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(`${baseUrl}/api/v1/platform/plans`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data && typeof (data as { detail?: unknown }).detail === "string"
        ? (data as { detail: string }).detail
        : `Backend error (${response.status}).`;
    throw new Error(detail);
  }
  return Array.isArray(data) ? (data as PlanCatalogItem[]) : [];
};

export const updatePlanCatalogItem = async (
  user: User,
  planId: PlanId,
  payload: {
    name: string;
    monthlyPrice?: number | null;
    currency?: string;
    billingType?: "fixed" | "quote";
    maxSalesPeople: number;
    maxCustomers: number;
    maxInventoryItems: number;
    maxSuppliers: number;
    maxAdmins: number;
    marketingFeatures: string[];
    highlighted: boolean;
    stripeProductId?: string | null;
    syncStripe?: boolean;
    intelligenceCapacityId?: "lite" | "plus" | "extra" | null;
    intelligenceDiscountPercent?: number | null;
  }
): Promise<PlanCatalogItem> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(`${baseUrl}/api/v1/platform/plans/${planId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const data = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data && typeof (data as { detail?: unknown }).detail === "string"
        ? (data as { detail: string }).detail
        : `Backend error (${response.status}).`;
    throw new Error(detail);
  }
  return data as PlanCatalogItem;
};
